import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  UserIcon,
  SettingsIcon,
  LogoutIcon,
  ShieldIcon,
  ChevronDownIcon,
} from './Icons';

export type UserRole = 'super_admin' | 'admin' | 'tenant_admin' | 'user';
export type ViewMode = 'frontend' | 'admin';

export interface UserMenuUser {
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

export interface UserMenuProps {
  user: UserMenuUser;
  currentView: ViewMode;
  onLogout: () => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToAdmin?: () => void;
  onNavigateToFrontend?: () => void;
  onNavigateToTenants?: () => void;
}

/**
 * Determine if user has admin privileges
 */
export function isAdmin(role: string): boolean {
  const normalizedRole = role.toLowerCase();
  return ['super_admin', 'superadmin', 'admin', 'tenant_admin'].includes(normalizedRole);
}

/**
 * Determine if user is a super admin
 */
export function isSuperAdmin(role: string): boolean {
  const normalizedRole = role.toLowerCase();
  return ['super_admin', 'superadmin'].includes(normalizedRole);
}

/**
 * Get user initials for avatar
 */
export function getUserInitials(name: string, email: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }
  return (email || 'U')[0].toUpperCase();
}

/**
 * Get role display label
 */
export function getRoleLabel(role: string): string {
  const normalizedRole = role.toLowerCase();
  switch (normalizedRole) {
    case 'super_admin':
    case 'superadmin':
      return 'Super Admin';
    case 'tenant_admin':
      return 'Tenant Admin';
    case 'admin':
      return 'Admin';
    case 'user':
    case 'member':
      return 'User';
    default:
      return role;
  }
}

const UserMenu: React.FC<UserMenuProps> = ({
  user,
  currentView,
  onLogout,
  onNavigateToProfile,
  onNavigateToSettings,
  onNavigateToAdmin,
  onNavigateToFrontend,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const userIsAdmin = isAdmin(user.role);
  const initials = getUserInitials(user.name, user.email);
  const roleLabel = getRoleLabel(user.role);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleMenuItemClick = useCallback((action: () => void) => {
    setIsOpen(false);
    action();
  }, []);

  return (
    <div className="user-menu-container">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        className={`user-menu-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        {/* Avatar */}
        <div className="user-menu-avatar">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
          {user.name || user.email}
        </span>
        <ChevronDownIcon size={16} className={`user-menu-chevron ${isOpen ? 'rotated' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="user-menu-dropdown"
          role="menu"
          aria-label="User menu options"
        >
          {/* User Info Header */}
          <div className="user-menu-header">
            <div className="user-menu-header-avatar">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div className="user-menu-header-info">
              <span className="user-menu-name">{user.name || 'User'}</span>
              <span className="user-menu-email">{user.email}</span>
              <span className="user-menu-role">{roleLabel}</span>
            </div>
          </div>

          <div className="user-menu-divider" />

          {/* Profile & Settings Section */}
          <div className="user-menu-section">
            <button
              className="user-menu-item"
              onClick={() => handleMenuItemClick(onNavigateToProfile)}
              role="menuitem"
            >
              <UserIcon size={18} />
              <span>My Profile</span>
            </button>
            {onNavigateToSettings && (
              <button
                className="user-menu-item"
                onClick={() => handleMenuItemClick(onNavigateToSettings)}
                role="menuitem"
              >
                <SettingsIcon size={18} />
                <span>Settings</span>
              </button>
            )}
          </div>

          {/* Admin Section - Only for admins */}
          {userIsAdmin && (
            <>
              <div className="user-menu-divider" />
              <div className="user-menu-section">
                {/* Admin Portal - view switching (frontend → admin) */}
                {currentView === 'frontend' && onNavigateToAdmin && (
                  <button
                    className="user-menu-item"
                    onClick={() => handleMenuItemClick(onNavigateToAdmin)}
                    role="menuitem"
                  >
                    <ShieldIcon size={18} />
                    <span>Admin Portal</span>
                  </button>
                )}

                {currentView === 'admin' && onNavigateToFrontend && (
                  <button
                    className="user-menu-item"
                    onClick={() => handleMenuItemClick(onNavigateToFrontend)}
                    role="menuitem"
                  >
                    <UserIcon size={18} />
                    <span>Application</span>
                  </button>
                )}
              </div>
            </>
          )}

          <div className="user-menu-divider" />

          {/* Logout Section */}
          <div className="user-menu-section">
            <button
              className="user-menu-item user-menu-item-danger"
              onClick={() => handleMenuItemClick(onLogout)}
              role="menuitem"
            >
              <LogoutIcon size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
